import { useEffect, useContext, useRef, useMemo } from 'react'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchFlightCalendar } from 'actions/FlightActions'
import { getFlightCalendarKey } from 'lib/flights/flightUtils'
import GeoContext from 'contexts/geoContext'

const SEARCH_RANGE_MONTHS = 6
const DEFAULT_TRIP_DAYS = 7
const SEARCH_START_OFFSET_DAYS = 2

export interface CheapestFlightInfo {
  price: number
  departDate: string
  returnDate: string
}

interface CalendarData {
  cachedPrices?: Record<string, { cost: number; fees: number }>
}

/**
 * Finds the cheapest date from a flight calendar response.
 * Returns the cheapest price, departure date, and a return date (departure + DEFAULT_TRIP_DAYS days).
 */
function findCheapestFromCalendar(calendar: CalendarData | undefined): CheapestFlightInfo | undefined {
  if (!calendar?.cachedPrices) return undefined

  const cheapest = Object.entries(calendar.cachedPrices).reduce<{ date: string; total: number } | undefined>(
    (best, [date, priceInfo]) => {
      const total = priceInfo.cost + priceInfo.fees
      if (total > 0 && (!best || total < best.total)) {
        return { date, total }
      }
      return best
    },
    undefined,
  )

  if (!cheapest) return undefined

  return {
    price: cheapest.total,
    departDate: cheapest.date,
    returnDate: moment(cheapest.date).add(DEFAULT_TRIP_DAYS, 'days').format(ISO_DATE_FORMAT),
  }
}

/**
 * Fetches calendar pricing for a list of destinations from a given origin,
 * and returns the cheapest price + date for each destination.
 */
function usePopularFlightPrices(
  originCode: string | undefined,
  destinations: Array<App.AirportLocation>,
): Record<string, { cheapest?: CheapestFlightInfo; loading: boolean }> {
  const dispatch = useAppDispatch()
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const flightCalendar = useAppSelector(state => state.flights.flightCalendar)
  const flightCalendarLoading = useAppSelector(state => state.flights.flightCalendarLoading)

  const startDateRef = useRef(moment().add(SEARCH_START_OFFSET_DAYS, 'days').format(ISO_DATE_FORMAT))
  const endDateRef = useRef(moment().add(SEARCH_RANGE_MONTHS, 'months').format(ISO_DATE_FORMAT))
  const startDate = startDateRef.current
  const endDate = endDateRef.current

  useEffect(() => {
    if (!originCode) return

    for (const dest of destinations) {
      dispatch(fetchFlightCalendar({
        startDate,
        endDate,
        origin: originCode,
        destination: dest.airportCode,
      }))
    }
  }, [dispatch, originCode, destinations, startDate, endDate])

  return useMemo(() => {
    const result: Record<string, { cheapest?: CheapestFlightInfo; loading: boolean }> = {}

    for (const dest of destinations) {
      const key = getFlightCalendarKey({
        startDate,
        endDate,
        origin: originCode ?? '',
        destination: dest.airportCode,
        region: currentRegionCode,
        currency: currentCurrency,
      })

      const calendar = flightCalendar[key]
      const loading = !!flightCalendarLoading[key]

      result[dest.airportCode] = {
        cheapest: findCheapestFromCalendar(calendar),
        loading,
      }
    }

    return result
  }, [destinations, startDate, endDate, originCode, currentRegionCode, currentCurrency, flightCalendar, flightCalendarLoading])
}

export default usePopularFlightPrices
